import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "../../components/shared/SectionHeading";
import { api, formatApiError } from "../../lib/api";
import { usePageMedia } from "../../lib/usePageMedia";
import { usePageTexts } from "../../lib/usePageTexts";

const EMPTY = {
  first_name: "", name: "", email: "", phone: "",
  subject: "Mariage", event_date: "", event_date_tbd: false,
  venue: "", guests: "", budget: "", message: "", company: "",
};

// v1.6 : chaque demande commerciale recueille nom, prénom, e-mail, téléphone,
// date de l'événement (ou « Date à définir »), type de prestation et description.
export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle"); // idle | sending | sent
  const pageMedia = usePageMedia();
  const { text } = usePageTexts();
  const hero = pageMedia["contact:hero"];

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/contact", {
        first_name: form.first_name, name: form.name, email: form.email, phone: form.phone,
        subject: form.subject, event_date: form.event_date || null, event_date_tbd: form.event_date_tbd,
        venue: form.venue || null, guests: form.guests || null, budget: form.budget || null,
        message: form.message, source: "contact", company: form.company,
      });
      setState("sent");
    } catch (err) {
      setState("idle");
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="container-ae py-12 md:py-16" data-testid="contact-page">
      <SectionHeading overline="Contact" title="Parlez-nous de" accent="votre projet">
        {text("contact:intro", "Une question, un projet de mariage ou d'événement ? Écrivez-nous — nous vous répondons rapidement.")}
      </SectionHeading>
      {hero && (
        <img src={hero.url} alt={hero.alt || "Contact Art Event"} className="mb-10 aspect-[21/9] w-full rounded-xl object-cover" data-testid="page-hero-image" />
      )}
      <div className="mt-10 grid gap-10 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-2">
          {[
            { icon: MapPin, label: "Adresse", value: "Artres (Nord) — retrait au dépôt sur rendez-vous" },
            { icon: Phone, label: "Téléphone", value: "06 88 60 83 53" },
            { icon: Mail, label: "E-mail", value: "contact@art-events.fr" },
            { icon: Clock, label: "Horaires", value: "Lundi au samedi, 9 h – 18 h" },
          ].map((c) => (
            <div key={c.label} className="flex items-start gap-4" data-testid={`contact-${c.label.toLowerCase()}`}>
              <div className="rounded-lg bg-sage-light p-3"><c.icon className="h-5 w-5 text-deepgreen" /></div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{c.label}</p>
                <p className="mt-1 text-sm font-semibold text-deepgreen">{c.value}</p>
              </div>
            </div>
          ))}
          <div className="rounded-xl bg-sage-light p-5 text-sm text-muted-foreground" data-testid="contact-zone">
            <p className="font-semibold text-deepgreen">Zone d'intervention</p>
            <p className="mt-1">Livraison jusqu'à 80 km autour d'Artres. Au-delà, contactez-nous pour une étude exceptionnelle — sans garantie de prestation.</p>
          </div>
        </div>
        {state === "sent" ? (
          <div className="card-ae p-6 md:p-8 lg:col-span-3" data-testid="contact-success">
            <p className="font-heading text-2xl text-deepgreen">Demande envoyée</p>
            <p className="mt-3 text-sm leading-relaxed text-foreground/80">
              Merci {form.first_name} — votre demande est bien enregistrée. Art Event vous répond du lundi au samedi, de 9 h à 18 h.
              {" "}Un e-mail de confirmation vient de vous être adressé.
            </p>
          </div>
        ) : (
          <form className="card-ae space-y-4 p-6 md:p-8 lg:col-span-3" onSubmit={submit} data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="c-prenom">Prénom *</label><input id="c-prenom" className="input-ae" required minLength={1} value={form.first_name} onChange={set("first_name")} data-testid="contact-first-name" /></div>
              <div><label className="label-ae" htmlFor="c-nom">Nom *</label><input id="c-nom" className="input-ae" required minLength={2} value={form.name} onChange={set("name")} data-testid="contact-name" /></div>
              <div><label className="label-ae" htmlFor="c-email">E-mail *</label><input id="c-email" type="email" className="input-ae" required value={form.email} onChange={set("email")} data-testid="contact-email" /></div>
              <div><label className="label-ae" htmlFor="c-tel">Téléphone *</label><input id="c-tel" type="tel" className="input-ae" required minLength={6} value={form.phone} onChange={set("phone")} data-testid="contact-phone" /></div>
              <div><label className="label-ae" htmlFor="c-type">Type d'événement / prestation *</label>
                <select id="c-type" className="input-ae" value={form.subject} onChange={set("subject")} data-testid="contact-subject">
                  <option>Mariage</option>
                  <option>Location de décoration / matériel</option>
                  <option>Coordination de mariage</option>
                  <option>Anniversaire / fête privée</option>
                  <option>Événement d'entreprise</option>
                  <option>Autre demande</option>
                </select>
              </div>
              <div>
                <label className="label-ae" htmlFor="c-date">Date de l'événement *</label>
                <input id="c-date" type="date" className="input-ae" required={!form.event_date_tbd} disabled={form.event_date_tbd} value={form.event_date} onChange={set("event_date")} data-testid="contact-event-date" />
                <label className="mt-1.5 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                  <input type="checkbox" checked={form.event_date_tbd} onChange={set("event_date_tbd")} className="h-4 w-4 accent-[#2C4C3B]" data-testid="contact-date-tbd" />
                  Date à définir — je ne connais pas encore la date
                </label>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div><label className="label-ae" htmlFor="c-lieu">Lieu (facultatif)</label><input id="c-lieu" className="input-ae" value={form.venue} onChange={set("venue")} data-testid="contact-venue" /></div>
              <div><label className="label-ae" htmlFor="c-invites">Invités (facultatif)</label><input id="c-invites" className="input-ae" placeholder="ex : 80" value={form.guests} onChange={set("guests")} data-testid="contact-guests" /></div>
              <div><label className="label-ae" htmlFor="c-budget">Budget indicatif (facultatif)</label><input id="c-budget" className="input-ae" placeholder="ex : 1500 €" value={form.budget} onChange={set("budget")} data-testid="contact-budget" /></div>
            </div>
            <div><label className="label-ae" htmlFor="c-message">Votre besoin *</label><textarea id="c-message" className="input-ae min-h-32" required minLength={10} placeholder="Décrivez votre projet, vos envies, le matériel recherché…" value={form.message} onChange={set("message")} data-testid="contact-message" /></div>
            <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="contact-company" />
            <button type="submit" className="btn-primary-ae disabled:opacity-60" disabled={state === "sending"} data-testid="contact-submit">
              <Send className="h-4 w-4" /> {state === "sending" ? "Envoi en cours…" : "Envoyer ma demande"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
